// `resolveColumnRef` and related ColumnRef runtime helpers, kept out of
// `value.ts` to avoid pulling
// `normalize.ts`/`Session` imports into the bottom of the dependency
// graph. The ColumnRef class itself stays in `value.ts` next to the
// rest of the `Value` union.

import type { SourcePos } from './ast.ts';
import { UnknownColumnRefError } from './errors.ts';
import { type AstInput, astShape, embeddedString, type OpCall } from './normalize.ts';
import { projectSchema } from './schema/types.ts';
import {
  type Cell,
  type ColumnRef,
  type Dataset,
  expectLazy,
  isMaterialized,
  LazyDataset,
  MaterializedDataset,
} from './value.ts';

/**
 * Resolve a `ColumnRef` to a projection carrying `ref.column`. Two steps:
 *
 *   1. **Validate the column exists** on `ref.ds`'s schema → missing
 *      → `UnknownColumnRefError` with the available column list as
 *      the suggestion set.
 *
 *   2. **Project via {@link projectSchema}.** This keeps `ref.column`
 *      plus the instance-identity columns (`space`/`externalId`) the
 *      source carries — the same shape the `columns` op produces. The
 *      lazy branch replays that op at materialisation time, so the
 *      declared `schemaAfter` MUST match it or the size-strict refine
 *      check in `materialize` rejects the plan. Consumers
 *      ({@link projectColumnRef}, dashboard tiles) read `ref.column` by
 *      name, so the retained identity columns ride along harmlessly.
 *      - `MaterializedDataset` → new `MaterializedDataset` carrying the
 *        projected columns with the matching schema.
 *      - `LazyDataset` → new `LazyDataset` with a `columns` plan node
 *        appended to the existing chain. The dataset arg (params[0]) is
 *        omitted from the stored args and re-prepended by the replay
 *        loop at materialisation time.
 *
 * `ref.path` is intentionally NOT acted on here. Paths walk into
 * struct-cell values, which is per-cell logic of the *consumer* (the
 * dashboard tile / qkg `in` clause that downstream-projects the leaf
 * value out of each cell). The col op's apply validates the path
 * against the schema at construction time, so a ref reaching
 * `resolveColumnRef` already has a schema-valid path; the path field
 * is preserved on the ref for the consumer to read.
 *
 * `pos` is the source-position threaded into any error raised by this
 * helper. Callers that resolve refs outside an expression context
 * (e.g., dashboard render time) pass `NO_POS`.
 */
export const resolveColumnRef = async (ref: ColumnRef, pos: SourcePos): Promise<Dataset> => {
  const context = `(col <dataset> ${ref.column})`;
  const dataset = ref.ds;

  // Step 1: validate the column exists on the schema.
  const colType = dataset.schema.get(ref.column);
  if (colType === undefined) {
    throw new UnknownColumnRefError(ref.column, Array.from(dataset.schema.keys()), pos);
  }

  // Step 2: project. `projectSchema` keeps `ref.column` plus any identity
  // columns the source carries, so the declared schema matches the `columns`
  // op the lazy branch replays (and the materialized branch below builds its
  // data from the same key set).
  const newSchema = projectSchema(dataset.schema, [ref.column]);
  if (isMaterialized(dataset)) {
    return new MaterializedDataset(
      // Build data from the projected schema's keys so the column set and
      // schema agree (the constructor enforces this). Every key is in the
      // source schema, so its data is present; the `?? []` fallback only
      // satisfies `noUncheckedIndexedAccess`.
      Object.fromEntries(
        Array.from(newSchema.keys()).map((name) => [name, (dataset.columns[name] ?? []) as readonly Cell[]]),
      ),
      newSchema,
      // The row set and order are preserved, so the identity axis passes
      // through unchanged.
      dataset.identity,
    );
  }
  // Lazy projection: append a `columns` plan node carrying the projected
  // schemaAfter. The plan-replay path executes this via the standard
  // `columns` op at materialisation time. The dataset arg (params[0]) is
  // omitted from the stored args and re-prepended by the replay loop.
  // `expectLazy` narrows the abstract `Dataset` (we already returned in the
  // materialized branch) to `LazyDataset` for `.plan` access. The name list
  // is an AST vector (`[:name]`) to match `(columns ds [:name …])`'s schema —
  // same shape the parser emits.
  const lazy = expectLazy(dataset, context, pos);
  const columnsCall: OpCall = { name: 'columns', params: [[{ $kw: ref.column }]] };
  return new LazyDataset(
    {
      source: lazy.plan.source,
      ops: [...lazy.plan.ops, { op: 'columns', args: columnsCall, schemaAfter: newSchema }],
    },
    newSchema,
  );
};
/**
 * Walk a struct sub-path into a single cell, yielding the leaf
 * scalar (`null` short-circuits to `null`). An ARRAY cell MAPS the
 * remaining path across every element — `(col % arr field)` projects
 * `field` off each element, yielding an `array<field>` (mirroring the
 * `array<struct>` field-projection type {@link walkFieldType} computes).
 * The SSOT path-walk kernel shared by {@link projectColumnRef} and the
 * `join` op's struct-sub-path key projection — keep both on this one
 * implementation so the null/short-circuit + MAP semantics never diverge.
 */
export const walkStructPath = (cell: unknown, path: readonly string[]): unknown => {
  if (path.length === 0 || cell == null) {
    return cell ?? null;
  }
  if (Array.isArray(cell)) {
    return cell.map((element) => walkStructPath(element, path));
  }
  const [seg, ...rest] = path;
  return walkStructPath((cell as Record<string, unknown>)[seg as string], rest);
};
/**
 * Resolve a `(col …)` / join-`:on` label list against a set of
 * available column names. The first label that names a column is the
 * column; any labels before it are an ignored (binding-name) prefix;
 * labels after it form the struct sub-path. `undefined` when no label
 * names a known column — callers decide the policy (the join op
 * throws a targeted error; the `(col …)` op's expression-context
 * inference falls back to `unknown`, and its per-row compile applies
 * a positional fallback).
 *
 * SSOT for the "which label is the column?" rule so the mental model
 * never diverges by context (join `:on` vs. `derive`/`filter`
 * expression bodies): the binding name is always optional and
 * irrelevant — the role / current dataset decides the source.
 */
export const pickColumnPath = (
  labels: readonly string[],
  hasColumn: (name: string) => boolean,
): { readonly column: string; readonly path: readonly string[] } | undefined => {
  const i = labels.findIndex(hasColumn);
  if (i === -1) {
    return undefined;
  }
  return { column: labels[i] as string, path: labels.slice(i + 1) };
};
/**
 * Tolerant `(col …)` label extractor: the symbol / string-literal
 * labels of a `(col …)` opCall node, or `undefined` when `node` is
 * not a `(col …)` call or any label is a non-literal (e.g. a Form-2
 * `(col (expr) …)` first arg). SSOT for "the name labels of a `(col
 * …)`" — shared by the `(col …)` expression-context compiler and the
 * predicate set-ref / per-row discriminator so the parse never
 * diverges by context. Strict callers (`(col …)` apply, join `:on`)
 * keep their own context-tailored error messages.
 */
export const colNodeLabels = (node: unknown): readonly string[] | undefined => {
  const s = astShape(node as AstInput);
  if (s.kind !== 'opCall' || s.call.name !== 'col') {
    return undefined;
  }
  const labels = (s.call.params ?? []).map((p) => {
    const ps = astShape(p as AstInput);
    if (ps.kind === 'keyword') {
      return ps.name;
    }
    if (ps.kind === 'symbolRef') {
      return ps.sym;
    }
    return embeddedString(p as AstInput);
  });
  return labels.every((l): l is string => l !== undefined) ? (labels as readonly string[]) : undefined;
};

/**
 * Resolve a `ColumnRef` to its per-cell, path-walked scalar cells —
 * the single source of truth for struct-path projection.
 *
 * Combines the three steps every consumer of a `ColumnRef` needs:
 *
 *   1. Project the ref to a single-column `Dataset` via
 *      {@link resolveColumnRef} (column-existence validation against
 *      the source Dataset's schema).
 *   2. Materialise that projection if it is lazy (`materialize`
 *      callback — pre-bound by the caller so this helper doesn't take
 *      an `EvalContext`; the predicate path passes `deps.materialize`,
 *      the React path passes the bound `useMaterialize`).
 *   3. Walk `ref.path` into each cell, yielding the leaf scalar value
 *      (the exact reduce kernel the set-ref pass `inlineSetRefs`
 *      relies on). `ref.path` is already schema-validated at `col`
 *      apply time, so the walk is plain, safe struct access — `null`
 *      short-circuits to `null`.
 *
 * Returns the column name (`ref.column`) plus the path-walked cell
 * array so callers that re-attach the projection as a synthetic
 * column (the `join :on` `(col …)` path) get the name without
 * re-deriving it.
 */
export const projectColumnRef = async (
  ref: ColumnRef,
  pos: SourcePos,
  materialize: (ds: LazyDataset) => Promise<MaterializedDataset>,
): Promise<{ readonly column: string; readonly cells: readonly Cell[] }> => {
  const projected = await resolveColumnRef(ref, pos);
  const mat = isMaterialized(projected)
    ? projected
    : await materialize(expectLazy(projected, `(col … ${ref.column})`, pos));
  const cells: readonly Cell[] = mat.columns[ref.column] ?? [];
  const walked = cells.map((cell) => walkStructPath(cell, ref.path));
  return { column: ref.column, cells: walked as readonly Cell[] };
};

// `ColumnRef.ds` is always a Dataset value (not a symbolic binding
// name), so resolution reduces to `ref.ds` and inlines into
// `resolveColumnRef`.
